<template>
    <div class="catalog-page">
        <!-- ================= HEADER ================= -->
        <div class="page-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap" style="gap: 16px;">
                <div class="d-flex align-items-center">
                    <div class="header-icon-wrapper mr-3">
                        <b-icon-box-seam class="header-icon" />
                    </div>
                    <div>
                        <h1 class="page-title">PRODUCT CATALOG</h1>
                        <p class="page-subtitle">
                            Real-time inventory tracking across
                            <strong>{{ getCategoryLabel(category_id) || 'All Categories' }}</strong>
                            • Showing {{ filteredItems.length }} of {{ allItems.length }} products
                        </p>
                    </div>
                </div>
                <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                    <b-badge variant="light" class="date-badge">
                        <b-icon-calendar class="mr-1" />{{ todayFormatted }}
                    </b-badge>
                    <b-button variant="outline-secondary" class="refresh-btn" @click="fetchProducts">
                        <b-icon-arrow-repeat class="mr-1" />Refresh
                    </b-button>
                </div>
            </div>
        </div>

        <!-- ================= FILTERS ================= -->
        <b-card no-body class="filters-card mb-4">
            <b-card-body class="py-3">
                <div v-if="isMobile" class="d-flex justify-content-between align-items-center mb-2">
                    <b-button size="md" variant="outline-primary" @click="filtersVisible = !filtersVisible">
                        <b-icon-funnel-fill class="mr-1" />
                        {{ filtersVisible ? "Hide Filters" : "Show Filters" }}
                    </b-button>
                </div>

                <b-collapse :visible="!isMobile || filtersVisible">
                    <div class="filter-grid">
                        <!-- Category -->
                        <div class="filter-item">
                            <label class="filter-label">
                                <b-icon-tag class="mr-1" />CATEGORY
                            </label>
                            <b-form-select
                                :options="packet.categories"
                                v-model="category_id"
                                @change="fetch_category"
                                size="md"
                                class="filter-select"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">All Categories</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Subcategory -->
                        <div class="filter-item">
                            <label class="filter-label">
                                <b-icon-diagram-3 class="mr-1" />SUBCATEGORY
                            </label>
                            <b-form-select
                                :options="subcategoryOptions"
                                v-model="subcategory_id"
                                @change="fetchSubcategory"
                                size="md"
                                class="filter-select"
                                :disabled="!category_id"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">All Subcategories</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Vendor -->
                        <div class="filter-item">
                            <label class="filter-label">
                                <b-icon-shop class="mr-1" />VENDOR
                            </label>
                            <b-form-select
                                :options="packet.vendors"
                                v-model="vendor_id"
                                @change="fetch_vendor"
                                size="md"
                                class="filter-select"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">All Vendors</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Stock Status -->
                        <div class="filter-item">
                            <label class="filter-label">
                                <b-icon-box-seam class="mr-1" />STOCK STATUS
                            </label>
                            <b-form-select
                                :options="stockStatusOptions"
                                v-model="stock_status"
                                @change="applyFilters"
                                size="md"
                                class="filter-select"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">All Stock</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Item Type -->
                        <div class="filter-item">
                            <label class="filter-label">
                                <b-icon-collection class="mr-1" />ITEM TYPE
                            </label>
                            <b-form-select
                                :options="itemTypeOptions"
                                v-model="item_type"
                                @change="applyFilters"
                                size="md"
                                class="filter-select"
                            >
                                <template #first>
                                    <b-form-select-option :value="null">All Types</b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Search -->
                        <div class="filter-item search-item">
                            <label class="filter-label">
                                <b-icon-search class="mr-1" />SEARCH PRODUCT
                            </label>
                            <div class="d-flex" style="gap: 8px;">
                                <b-input
                                    v-model="product_query"
                                    size="md"
                                    placeholder="Name or SKU..."
                                    class="filter-input"
                                    @keyup.enter="fetch_product_name_sku"
                                ></b-input>
                                <b-button variant="primary" size="md" class="search-btn" @click="fetch_product_name_sku">
                                    <b-icon-search />
                                </b-button>
                            </div>
                        </div>

                        <!-- Clear -->
                        <div class="filter-item">
                            <label class="filter-label">&nbsp;</label>
                            <b-button variant="outline-warning" size="md" class="w-100 clear-btn" @click="clearFilters">
                                <b-icon-x-circle class="mr-1" />Clear Filters
                            </b-button>
                        </div>
                    </div>
                </b-collapse>
            </b-card-body>
        </b-card>

        <!-- Active Filters -->
        <b-card v-if="hasActiveFilters" no-body class="active-filters-card mb-4">
            <b-card-body class="py-3">
                <div class="d-flex align-items-center flex-wrap" style="gap: 12px;">
                    <strong style="font-size: 15px;">Active Filters:</strong>
                    <b-badge v-if="stock_status" variant="info" class="active-badge">
                        Stock: {{ getStockStatusLabel(stock_status) }}
                        <b-icon-x @click="removeFilter('stock_status')" class="ml-1 filter-x" />
                    </b-badge>
                    <b-badge v-if="item_type" variant="info" class="active-badge">
                        Type: {{ getItemTypeLabel(item_type) }}
                        <b-icon-x @click="removeFilter('item_type')" class="ml-1 filter-x" />
                    </b-badge>
                    <b-badge v-if="category_id" variant="info" class="active-badge">
                        Category: {{ getCategoryLabel(category_id) }}
                        <b-icon-x @click="removeFilter('category_id')" class="ml-1 filter-x" />
                    </b-badge>
                    <b-badge v-if="subcategory_id" variant="info" class="active-badge">
                        Subcategory: {{ getSubcategoryLabel(subcategory_id) }}
                        <b-icon-x @click="removeFilter('subcategory_id')" class="ml-1 filter-x" />
                    </b-badge>
                    <b-badge v-if="vendor_id" variant="info" class="active-badge">
                        Vendor: {{ getVendorLabel(vendor_id) }}
                        <b-icon-x @click="removeFilter('vendor_id')" class="ml-1 filter-x" />
                    </b-badge>
                    <b-badge v-if="product_query" variant="info" class="active-badge">
                        Search: {{ product_query }}
                        <b-icon-x @click="removeFilter('product_query')" class="ml-1 filter-x" />
                    </b-badge>
                </div>
            </b-card-body>
        </b-card>

        <!-- ================= LOADING ================= -->
        <div v-if="items_being_loaded" class="text-center py-5">
            <b-spinner variant="primary" type="grow" style="width: 3rem; height: 3rem;"></b-spinner>
            <p class="mt-3 text-muted" style="font-size: 18px;">Loading products...</p>
        </div>

        <div v-else>
            <!-- ================= KPI CARDS ================= -->
            <div class="kpi-grid">
                <!-- Total Value (DARK) -->
                <div class="kpi-card dark accent-green">
                    <div class="kpi-icon-wrap icon-green">
                        <b-icon-graph-up />
                    </div>
                    <span class="kpi-label">Total Value</span>
                    <span class="kpi-value">{{ formatCurrency(totalInventoryValue) }}</span>
                    <div class="kpi-trend trend-green">
                        <b-icon-cash-stack />
                        Across {{ filteredItems.length }} items
                    </div>
                </div>

                <!-- In Stock -->
                <div class="kpi-card accent-blue">
                    <div class="kpi-icon-wrap icon-blue">
                        <b-icon-box-seam />
                    </div>
                    <span class="kpi-label">In Stock</span>
                    <span class="kpi-value">{{ inStockCount }}</span>
                    <div class="kpi-trend trend-blue">
                        <b-icon-check-circle />
                        Items available
                    </div>
                </div>

                <!-- Out of Stock -->
                <div class="kpi-card accent-red">
                    <div class="kpi-icon-wrap icon-red">
                        <b-icon-exclamation-triangle />
                    </div>
                    <span class="kpi-label">Out of Stock</span>
                    <span class="kpi-value">{{ outOfStockCount }}</span>
                    <div class="kpi-trend trend-red">
                        <b-icon-x-circle />
                        Need restock
                    </div>
                </div>

                <!-- Stock Health -->
                <div class="kpi-card accent-amber">
                    <div class="kpi-icon-wrap icon-amber">
                        <b-icon-shield-check />
                    </div>
                    <span class="kpi-label">Stock Health</span>
                    <span class="kpi-value">{{ stockHealthPercent }}%</span>
                    <div class="kpi-trend" :class="stockHealthPercent >= 75 ? 'trend-green' : stockHealthPercent >= 50 ? 'trend-amber' : 'trend-red'">
                        <b-icon-percent />
                        {{ stockHealthLabel }}
                    </div>
                </div>
            </div>

            <!-- ================= PRODUCTS SECTION ================= -->
            <div v-if="filteredItems.length > 0">
                <div class="products-header">
                    <h2 class="section-title">Products</h2>
                    <div class="d-flex align-items-center" style="gap: 12px;">
                        <span class="section-subtitle">
                            Showing {{ filteredItems.length }} of {{ allItems.length }} items
                        </span>
                    </div>
                </div>

                <div class="products-grid">
                    <div
                        v-for="(item, idx) in filteredItems"
                        :key="idx"
                        class="product-card"
                        @click="goToProductPage(item)"
                    >
                        <!-- Category Badge -->
                        <div class="product-category-pill" :class="getCategoryPillClass(item)">
                            <b-icon-tag class="mr-1" size="sm" />
                            {{ item.category || 'Uncategorized' }}
                        </div>

                        <!-- Image (NOW USING PROXY) -->
                        <div class="product-image-box">
                            <img
                                v-if="getProductImage(item)"
                                :src="getProductImage(item)"
                                :alt="item.product_name"
                                class="product-image"
                                @error="handleImageError($event, item)"
                            />
                            <div v-else class="product-image-fallback">
                                <b-icon-image font-scale="3" />
                                <span>No Image</span>
                            </div>
                        </div>

                        <!-- Name & SKU -->
                        <h6 class="product-name" :title="item.product_name">{{ item.product_name }}</h6>
                        <p class="product-sku">SKU: {{ item.product_sku }}</p>

                        <!-- Price + Stock Badge Row -->
                        <div class="product-price-row">
                            <div class="product-price">
                                ${{ parseFloat(item.selling_price).toFixed(2) }}
                            </div>
                            <div class="product-stock-badge" :class="item.availability > 0 ? 'stock-in' : 'stock-out'">
                                <b-icon-check-circle-fill v-if="item.availability > 0" size="sm" />
                                <b-icon-x-circle-fill v-else size="sm" />
                                <span>{{ item.availability > 0 ? 'In Stock ' + item.availability : 'Out of Stock' }}</span>
                            </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="product-actions">
                            <b-button
                                variant="outline-secondary"
                                size="md"
                                class="action-btn view-btn"
                                @click.stop="goToProductPage(item)"
                            >
                                <b-icon-eye class="mr-1" size="sm" />
                                View
                            </b-button>
                            <b-button
                                v-if="item.children.length > 0"
                                variant="primary"
                                size="md"
                                class="action-btn components-btn"
                                @click.stop="viewComponents(idx)"
                            >
                                <b-icon-diagram-3 class="mr-1" size="sm" />
                                {{ item.children.length }} Components
                            </b-button>
                            <b-button
                                v-else
                                variant="primary"
                                size="md"
                                class="action-btn components-btn"
                                @click.stop="checkItemAvailability(idx)"
                            >
                                <b-icon-box-seam class="mr-1" size="sm" />
                                Availability
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Modals -->
                <b-modal id="modal-availability" scrollable title="" size="md" hide-footer>
                    <Availability :viewdata="{availability_data:availability_data, product_id: q_product_id, product_sku: q_product_sku}" :locationsdict="locationsdict" :sublocationsdict="sublocationsdict" />
                </b-modal>

                <b-modal id="modal-children" scrollable title="Child Products" size="md" hide-footer>
                    <Children :viewdata="components_data" />
                </b-modal>
            </div>

            <!-- No Results -->
            <div v-else class="text-center py-5">
                <b-alert variant="warning" show class="mb-0" style="font-size: 18px;">
                    <b-icon-exclamation-triangle class="mr-2" />
                    NO PRODUCTS FOUND MATCHING YOUR FILTERS
                </b-alert>
            </div>
        </div>
    </div>
</template>

<script>
    import global_vars from '@/utilities/global_vars'
    import moment from 'moment'
    import Backer from '@/components/globals/Backer'
    import Availability from "@/components/importables/Availability"
    import Children from "@/components/importables/Children"
    import Requestor from '@/utilities/Requestor'

    import {
        BIconFunnelFill,
        BIconBoxSeam,
        BIconGraphUp,
        BIconCheckCircle,
        BIconExclamationTriangle,
        BIconXCircle,
        BIconShieldCheck,
        BIconPercent,
        BIconCashStack,
        BIconCalendar,
        BIconArrowRepeat,
        BIconTag,
        BIconDiagram3,
        BIconShop,
        BIconCollection,
        BIconSearch,
        BIconXCircleFill,
        BIconCheckCircleFill,
        BIconImage,
        BIconEye,
        BIconX
    } from "bootstrap-vue";

    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ProductCatalog",
        props: ["showbacker"],
        components: {
            Backer, Loading, Availability, Children,
            BIconFunnelFill, BIconBoxSeam, BIconGraphUp, BIconCheckCircle,
            BIconExclamationTriangle, BIconXCircle, BIconShieldCheck,
            BIconPercent, BIconCashStack, BIconCalendar, BIconArrowRepeat,
            BIconTag, BIconDiagram3, BIconShop, BIconCollection, BIconSearch,
            BIconXCircleFill, BIconCheckCircleFill, BIconImage, BIconEye, BIconX
        },
        data: function(){
            return {
                userDetails: {properties: {}},
                locations: [],
                locationsdict: {},
                sublocations: [],
                sublocationsdict: {},
                subcategory_id: null,
                subcategories: [],
                tr_item_statuses: global_vars.TRANSFER_STATUSES,
                tr_item_statuses_colors: global_vars.TRANSFER_STATUSES_COLORS_DICT,
                tr_item_statuses_dict: global_vars.TRANSFER_STATUSES_DICT,
                items: [],
                allItems: [],
                items_being_loaded: false,
                sort_asc: true,
                actions_disabled: false,
                category_id: null,
                vendor_id: null,
                q_product_id: null,
                q_product_sku: null,
                availability_data: [],
                stock_status: null,
                item_type: null,
                components_data: [],
                stockStatusOptions: [
                    { value: 'in_stock', text: 'In Stock' },
                    { value: 'out_of_stock', text: 'Out of Stock' }
                ],
                itemTypeOptions: [
                    { value: 'single', text: 'Single Items' },
                    { value: 'group', text: 'Group Items' }
                ],
                fetchingData: false,
                product_query: null,
                packet: {},
                filtersVisible: false,
                isMobile: false,
            }
        },
        computed: {
            subcategoryOptions: function() {
                if (!this.category_id) {
                    return [{ value: null, text: 'Select Category First' }];
                }
                const filteredSubcategories = this.subcategories.filter(
                    subcat => subcat.category_id == this.category_id
                );
                return [
                    { value: null, text: 'ALL' },
                    ...filteredSubcategories.map(subcat => ({
                        value: subcat.id,
                        text: subcat.subcategory
                    }))
                ];
            },

            isAdmin: function() {
                if (this.packet.userDetails && this.packet.userDetails.properties) {
                    return this.packet.userDetails.userrole === 'admin' ||
                           this.packet.userDetails.properties.is_admin === true;
                }
                return false;
            },

            hasActiveFilters: function() {
                return this.stock_status !== null ||
                       this.item_type !== null ||
                       this.category_id !== null ||
                       this.subcategory_id !== null ||
                       this.vendor_id !== null ||
                       (this.product_query && this.product_query.length > 0);
            },

            filteredItems: function() {
                let filtered = [...this.allItems];
                if (this.stock_status === 'in_stock') {
                    filtered = filtered.filter(item => item.availability > 0);
                } else if (this.stock_status === 'out_of_stock') {
                    filtered = filtered.filter(item => item.availability === 0);
                }
                if (this.item_type === 'single') {
                    filtered = filtered.filter(item => item.children.length === 0);
                } else if (this.item_type === 'group') {
                    filtered = filtered.filter(item => item.children.length > 0);
                }
                return filtered;
            },

            /* ============ KPI COMPUTED PROPERTIES ============ */
            totalInventoryValue: function() {
                return this.filteredItems.reduce((sum, item) => {
                    const price = parseFloat(item.selling_price || 0);
                    const qty = parseInt(item.availability || 0);
                    return sum + (price * qty);
                }, 0);
            },

            inStockCount: function() {
                return this.filteredItems.filter(item => item.availability > 0).length;
            },

            outOfStockCount: function() {
                return this.filteredItems.filter(item => item.availability === 0).length;
            },

            stockHealthPercent: function() {
                const total = this.filteredItems.length;
                if (total === 0) return 0;
                return ((this.inStockCount / total) * 100).toFixed(1);
            },

            stockHealthLabel: function() {
                const pct = parseFloat(this.stockHealthPercent);
                if (pct >= 90) return 'Excellent';
                if (pct >= 75) return 'Good';
                if (pct >= 50) return 'Fair';
                return 'Poor';
            },

            todayFormatted: function() {
                return moment().format('MMM D, YYYY • h:mm A');
            }
        },
        methods: {
            formatCurrency(value) {
                const num = parseFloat(value || 0);
                if (isNaN(num)) return '$0.00';
                return num.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            },

            getCategoryPillClass(item) {
                const cat = (item.category || '').toLowerCase();
                if (cat.includes('bed')) return 'pill-blue';
                if (cat.includes('furn')) return 'pill-orange';
                if (cat.includes('storage')) return 'pill-purple';
                if (cat.includes('dining')) return 'pill-green';
                return 'pill-gray';
            },

            /* =========================================================
               IMAGE PROXY HELPER
               Routes fmapi.myfurnituremecca.com images through the
               backend proxy to avoid CORS / mixed-content issues.
               ========================================================= */
            getProductImage(item) {
                if (!item || !item.image_url) return null;
                let imageUrl = item.image_url.trim();
                if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') return null;
                if (imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return `/api/image/image-proxy/?url=${encodeURIComponent(imageUrl)}`;
                }
                return imageUrl;
            },

            fetchSubcategories: function() {
                let that = this;
                let path = "/api/product/get_subcategories/";
                let data = {};
                if (this.category_id) {
                    data.category_id = this.category_id;
                }
                let s = function(response) {
                    if (response.data && !response.data.error) {
                        that.subcategories = response.data.result || [];
                    } else {
                        that.$bvToast.toast(
                            response.data.error || "Error loading subcategories",
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                };
                let f = function(error) {
                    console.error("Error loading subcategories:", error);
                };
                Requestor.postSyncP(path, data, this, s, f);
            },

            fetchSubcategory: function() {
                let that = this;
                let data = {};
                if (this.category_id) {
                    data.category_id = this.category_id;
                }
                if (this.subcategory_id) {
                    data.subcategory_id = this.subcategory_id;
                }
                if (this.vendor_id != null) {
                    data.vendor_id = this.vendor_id;
                }
                let path = "/api/product/get_products_for_catalog/";
                let s = function(response) {
                    if (response.data.result) {
                        that.allItems = response.data.result;
                        that.items = [...that.allItems];
                    } else {
                        that.$bvToast.toast(
                            response.data.error,
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                let f = function(error) {
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading products.",
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                this.items_being_loaded = true;
                this.actions_disabled = true;
                this.product_query = null;
                Requestor.postSyncP(path, data, this, s, f);
            },

            handleResize() {
                this.isMobile = window.innerWidth < 768;
            },

            applyFilters: function() {
                console.log("Filters applied:", {
                    stock_status: this.stock_status,
                    item_type: this.item_type
                });
            },

            clearFilters: function() {
                this.stock_status = null;
                this.item_type = null;
                this.category_id = null;
                this.subcategory_id = null;
                this.vendor_id = null;
                this.product_query = null;
                this.items = [...this.allItems];
            },

            removeFilter: function(filterType) {
                if (filterType === 'stock_status') {
                    this.stock_status = null;
                } else if (filterType === 'item_type') {
                    this.item_type = null;
                } else if (filterType === 'category_id') {
                    this.category_id = null;
                    this.subcategory_id = null;
                } else if (filterType === 'subcategory_id') {
                    this.subcategory_id = null;
                    this.fetchSubcategory();
                } else if (filterType === 'vendor_id') {
                    this.vendor_id = null;
                    this.fetchProducts();
                } else if (filterType === 'product_query') {
                    this.product_query = null;
                    this.fetchProducts();
                }
            },

            getSubcategoryLabel: function(subcategoryId) {
                const subcategory = this.subcategories.find(sub => sub.id === subcategoryId);
                return subcategory ? subcategory.subcategory : subcategoryId;
            },

            getStockStatusLabel: function(status) {
                const option = this.stockStatusOptions.find(opt => opt.value === status);
                return option ? option.text : status;
            },

            getItemTypeLabel: function(type) {
                const option = this.itemTypeOptions.find(opt => opt.value === type);
                return option ? option.text : type;
            },

            getCategoryLabel: function(categoryId) {
                if (!categoryId || !this.packet.categories) return null;
                const category = this.packet.categories.find(cat => cat.value === categoryId);
                return category ? category.text : categoryId;
            },

            getVendorLabel: function(vendorId) {
                const vendor = this.packet.vendors.find(vend => vend.value === vendorId);
                return vendor ? vendor.text : vendorId;
            },

            getProductUrl(item) {
                if (item.slug) {
                    return `https://fmapi.myfurnituremecca.com/product/${item.slug}`;
                }
                return `https://fmapi.myfurnituremecca.com/product/${item.product_sku}`;
            },

            goToProductPage(item) {
                const url = this.getProductUrl(item);
                if (url) {
                    window.open(url, '_blank');
                } else {
                    this.$bvToast.toast(
                        'Product URL not available',
                        {title: "Error", autoHideDelay: 3000, variant:"warning"}
                    );
                }
            },

            handleImageError(event, item) {
                if (item) {
                    console.warn(`Image failed to load for ${item.product_sku || item.sku}:`, event.target.src);
                }
                event.target.style.display = 'none';
                const parent = event.target.parentElement;
                if (parent && !parent.querySelector('.image-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'image-fallback';
                    fallback.style.cssText = 'color: #6c757d; font-size: 14px; width: 100%; text-align: center;';
                    fallback.textContent = 'Image Not Available';
                    parent.appendChild(fallback);
                }
            },

            fetchImagesAndSlugs: function() {
                let that = this;
                this.$bvModal.msgBoxConfirm('This will fetch image URLs and slugs from API for all products. Continue?', {
                    title: 'Confirm Fetch', size: 'sm', buttonSize: 'sm',
                    okVariant: 'primary', okTitle: 'YES', cancelTitle: 'NO',
                    footerClass: 'p-2', hideHeaderClose: false, centered: true
                }).then(confirmed => {
                    if (confirmed) that.executeFetch();
                });
            },

            executeFetch: function() {
                let that = this;
                this.fetchingData = true;
                let path = "/api/product/fetch_images_and_slugs/";
                let data = {};
                let s = function(response) {
                    that.fetchingData = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 8000, variant:"danger"});
                    } else {
                        const result = response.data.result;
                        const total = result.updated + result.failed;
                        let message = `Processed ${total} products: ${result.updated} updated, ${result.failed} failed.`;
                        that.$bvToast.toast(message, {title: "Fetch Complete", autoHideDelay: 8000, variant:"success"});
                        that.fetchProducts();
                    }
                };
                let f = function(error) {
                    that.fetchingData = false;
                    that.$bvToast.toast("Error fetching data from API.", {title: "Error", autoHideDelay: 8000, variant:"danger"});
                };
                Requestor.postSyncP(path, data, this, s, f);
            },

            findMatchingSKUs: function() {
                let that = this;
                this.$bvModal.msgBoxConfirm('This will test a sample of SKUs. Continue?', {
                    title: 'Find Matching SKUs', size: 'sm', buttonSize: 'sm',
                    okVariant: 'primary', okTitle: 'YES', cancelTitle: 'NO',
                    footerClass: 'p-2', hideHeaderClose: false, centered: true
                }).then(confirmed => {
                    if (confirmed) that.executeFindMatchingSKUs();
                });
            },

            executeFindMatchingSKUs: function() {
                let that = this;
                this.fetchingData = true;
                let path = "/api/product/find_matching_skus/";
                let data = {};
                let s = function(response) {
                    that.fetchingData = false;
                    if (response.data.error) {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 5000, variant:"danger"});
                    } else {
                        const result = response.data.result;
                        let message = `Tested ${result.total_tested} SKUs: ${result.found.length} found, ${result.not_found.length} not found.`;
                        that.$bvToast.toast(message, {title: "Matching SKUs Found", autoHideDelay: 8000, variant:"success"});
                    }
                };
                let f = function(error) {
                    that.fetchingData = false;
                    that.$bvToast.toast("Error finding matching SKUs.", {title: "Error", autoHideDelay: 5000, variant:"danger"});
                };
                Requestor.postSyncP(path, data, this, s, f);
            },

            testSingleSKU: function() {
                let testSKU = "1610-65L-75-65R";
                let path = "/api/product/test_single_sku/";
                let data = {sku: testSKU};
                let s = function(response) { console.log("Test API response:", response.data); };
                let f = function(error) { console.error("Test API error:", error); };
                Requestor.postSyncP(path, data, this, s, f);
            },

            logger: function(e){
                console.log(this.selected_sublocations);
                console.log(this.vendor_id);
            },

            fetch_prereqs: function(){
                let that = this;
                let prereqs = [{
                    path: "/api/user/get_tools_packet/",
                    data: {},
                    type: "",
                    executable: Requestor.getSync,
                    s: function(response){
                        if (response.data.result){
                            that.categories = response.data.result.categories;
                            that.vendors = response.data.result.vendors;
                            that.locations = response.data.result.locations.map(d => ({value: d.id, text: d.name}));
                            that.locations.splice(0, 0, {value: null, text: "All"});
                            that.locationsdict = that.locations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
                            that.sublocations = response.data.result.sublocations.map(d => ({value: d.id, text: d.name, location_id: d.location_id}));
                            that.sublocationsdict = that.sublocations.reduce((map, obj) => (map[obj.value] = obj.text, map), {});
                        } else {
                            that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                        }
                    },
                    f: function(error){
                        console.log(error);
                        that.$bvToast.toast("Error loading locations.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                }];
                for (var i=0; i<prereqs.length; i++){
                    let path = prereqs[i]["path"];
                    let data = prereqs[i]["data"];
                    let s = prereqs[i]["s"];
                    let f = prereqs[i]["f"];
                    let type = prereqs[i]["type"];
                    if (type == "secure"){
                        prereqs[i]["executable"](path, data, this, s, f);
                    } else {
                        prereqs[i]["executable"](path, data, s, f);
                    }
                }
            },

            fetch_category: function(){
                let that = this;
                let data = { category_id: this.category_id };
                this.subcategory_id = null;
                if (this.vendor_id != null) data.vendor_id = this.vendor_id;
                let path = "/api/product/get_products_for_catalog/";
                let s = function(response){
                    if (response.data.result){
                        that.allItems = response.data.result;
                        that.items = [...that.allItems];
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast("Error loading products.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                this.items_being_loaded = true;
                this.actions_disabled = true;
                this.product_query = null;
                Requestor.postSyncP(path, data, this, s, f);
            },

            fetch_vendor: function(){
                let that = this;
                let data = { vendor_id: this.vendor_id };
                if (this.category_id != null) data.category_id = this.category_id;
                let path = "/api/product/get_products_for_catalog/";
                let s = function(response){
                    if (response.data.result){
                        that.allItems = response.data.result;
                        that.items = [...that.allItems];
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast("Error loading products.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                this.items_being_loaded = true;
                this.actions_disabled = true;
                this.product_query = null;
                Requestor.postSyncP(path, data, this, s, f);
            },

            fetch_product_name_sku: function(){
                let that = this;
                if (!this.product_query || this.product_query.length < 2){
                    this.$bvToast.toast("Please specify valid product name or sku.", {title: "Warning", variant: "warning"});
                    return;
                }
                let data = { product_query: this.product_query };
                let path = "/api/product/get_products_for_catalog/";
                let s = function(response){
                    if (response.data.result){
                        that.allItems = response.data.result;
                        that.items = [...that.allItems];
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast("Error loading products.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                this.items_being_loaded = true;
                this.actions_disabled = true;
                this.category_id = null;
                this.vendor_id = null;
                Requestor.postSyncP(path, data, this, s, f);
            },

            fetchProducts: function(e){
                if (e) e.preventDefault();
                let that = this;
                let data = {
                    category_id: this.category_id,
                    product_query: (this.product_query || "").toLowerCase().replace("'", "").replace(";", ""),
                    limit: 600
                };
                let path = "/api/product/get_products_for_catalog/";
                let s = function(response){
                    if (response.data.result){
                        that.allItems = response.data.result;
                        that.items = [...that.allItems];
                    } else {
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    }
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                let f = function(error){
                    console.log(error);
                    that.$bvToast.toast("Error loading products.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    that.items_being_loaded = false;
                    that.actions_disabled = false;
                };
                this.items_being_loaded = true;
                this.actions_disabled = true;
                Requestor.postSyncP(path, data, this, s, f);
            },

            sortby: function(key){
                if (this.sort_asc){
                    this.items = Requestor.sort(this.items, key);
                } else {
                    this.items = Requestor.sort_reverse(this.items, key);
                }
                this.sort_asc = !this.sort_asc;
            },

            checkItemAvailability: function(idx){
                let path = "/api/inventory/get_tr_item_availability/";
                let item = this.items[idx];
                let data = {product_id: item.product_id, product_sku: item.product_sku};
                let that = this;
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(response.data.error, {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    } else {
                        that.availability_data = response.data.result;
                        that.q_product_id = item.product_id;
                        that.q_product_sku = item.product_sku;
                        that.$bvModal.show("modal-availability");
                    }
                    that.is_action_disabled = false;
                };
                let f = function(error){
                    that.$bvToast.toast("An error occurred trying to fetch product availability. Please try again.", {title: "Error", autoHideDelay: 1500, variant:"warning"});
                    that.is_action_disabled = false;
                };
                this.is_action_disabled = true;
                Requestor.getAsync(path, data, s, f);
            },

            viewComponents: function(idx){
                let children = this.items[idx].children;
                this.components_data = children;
                this.$bvModal.show("modal-children");
            }
        },
        mounted: function() {
            this.handleResize();
            window.addEventListener("resize", this.handleResize);
        },
        beforeDestroy() {
            window.removeEventListener("resize", this.handleResize);
        },
        created: function(){
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.fetchSubcategories();
        }
    }
</script>

<style scoped>
/* =========================================================
   PAGE WRAPPER
   ========================================================= */
.catalog-page {
    max-width: 1600px;
    width: 90%;
    margin-left: auto;
    margin-right: auto;
    margin-top: 16px;
    padding-bottom: 40px;
}

/* =========================================================
   HEADER
   ========================================================= */
.page-header {
    margin-bottom: 20px;
}

.header-icon-wrapper {
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #1e293b, #0f172a);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.25);
}

.header-icon {
    font-size: 32px;
    color: #fff;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.08); }
}

.page-title {
    font-size: 42px;
    font-weight: 800;
    color: #0b1220;
    margin: 0 0 6px 0;
    letter-spacing: -1px;
}

.page-subtitle {
    font-size: 16px;
    color: #64748b;
    margin: 0;
}

.date-badge {
    background-color: #f1f5f9;
    color: #1e293b;
    border: 1px solid #e2e8f0;
    padding: 10px 18px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
}

.refresh-btn {
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    padding: 10px 22px;
}

/* =========================================================
   FILTERS
   ========================================================= */
.filters-card {
    border: none;
    border-radius: 18px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    background: white;
    position: relative;
    overflow: hidden;
}

.filters-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
}

.filter-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 16px;
}

.filter-item {
    min-width: 0;
}

.filter-item.search-item {
    grid-column: span 1;
}

.filter-label {
    font-size: 12px;
    font-weight: 700;
    color: #475569;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 8px;
    display: block;
}

.filter-select,
.filter-input {
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    font-size: 15px;
    padding: 10px 14px;
    background: #f8fafc;
    height: 44px;
}

.filter-select:focus,
.filter-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
    background: #fff;
}

.search-btn {
    border-radius: 12px;
    min-width: 48px;
    height: 44px;
}

.clear-btn {
    border-radius: 12px;
    height: 44px;
    font-weight: 600;
}

/* =========================================================
   ACTIVE FILTERS
   ========================================================= */
.active-filters-card {
    border: none;
    border-radius: 14px;
    background: #f1f5f9;
    box-shadow: none;
}

.active-badge {
    font-size: 13px;
    padding: 8px 14px;
    border-radius: 20px;
    font-weight: 600;
}

.filter-x {
    cursor: pointer;
    opacity: 0.7;
}

.filter-x:hover {
    opacity: 1;
}

/* =========================================================
   KPI CARDS
   ========================================================= */
.kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-bottom: 32px;
}

.kpi-card {
    background: #fff;
    border-radius: 20px;
    padding: 36px 24px 30px;
    border: 1px solid #e8edf3;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    transition: all 0.2s ease;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
    position: relative;
    overflow: hidden;
}

.kpi-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
}

.kpi-card:hover {
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
    transform: translateY(-2px);
}

.kpi-card.dark {
    background: #0b1220;
    border-color: #0b1220;
    box-shadow: 0 8px 26px rgba(11, 18, 32, 0.35);
}

.kpi-card.dark .kpi-label { color: rgba(255, 255, 255, 0.65); }
.kpi-card.dark .kpi-value { color: #ffffff; }

.accent-green::before { background: linear-gradient(90deg, #10b981, #34d399); }
.accent-blue::before  { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
.accent-red::before   { background: linear-gradient(90deg, #ef4444, #f87171); }
.accent-amber::before { background: linear-gradient(90deg, #f59e0b, #fbbf24); }

.kpi-icon-wrap {
    width: 76px;
    height: 76px;
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 34px;
    margin-bottom: 22px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.icon-green { background: linear-gradient(135deg, #10b981, #34d399); }
.icon-blue  { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.icon-red   { background: linear-gradient(135deg, #ef4444, #f87171); }
.icon-amber { background: linear-gradient(135deg, #f59e0b, #fbbf24); }

.kpi-label {
    font-size: 18px;
    color: #64748b;
    font-weight: 500;
    margin-bottom: 12px;
    letter-spacing: 0.2px;
}

.kpi-value {
    font-size: 44px;
    font-weight: 800;
    color: #0b1220;
    line-height: 1.1;
    letter-spacing: -1.5px;
    margin-bottom: 18px;
    word-break: break-word;
}

.kpi-trend {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    padding: 8px 18px;
    border-radius: 999px;
    white-space: nowrap;
}

.trend-green { color: #047857; background: #d1fae5; }
.trend-blue  { color: #1d4ed8; background: #dbeafe; }
.trend-red   { color: #b91c1c; background: #fee2e2; }
.trend-amber { color: #92400e; background: #fef3c7; }

.kpi-card.dark .kpi-trend.trend-green {
    color: #34d399;
    background: rgba(16, 185, 129, 0.18);
}

/* =========================================================
   PRODUCTS SECTION
   ========================================================= */
.products-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 12px;
}

.section-title {
    font-size: 36px;
    font-weight: 800;
    color: #0b1220;
    margin: 0;
    letter-spacing: -0.8px;
}

.section-subtitle {
    font-size: 15px;
    color: #64748b;
    font-weight: 500;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-bottom: 24px;
}

/* =========================================================
   PRODUCT CARD
   ========================================================= */
.product-card {
    background: #fff;
    border-radius: 20px;
    padding: 20px;
    border: 1px solid #e8edf3;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.product-card:hover {
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
    transform: translateY(-3px);
}

.product-category-pill {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-bottom: 16px;
    align-self: flex-start;
    text-transform: uppercase;
}

.pill-blue   { background: #dbeafe; color: #1d4ed8; }
.pill-orange { background: #fed7aa; color: #c2410c; }
.pill-purple { background: #ede9fe; color: #6d28d9; }
.pill-green  { background: #d1fae5; color: #047857; }
.pill-gray   { background: #e2e8f0; color: #475569; }

.product-image-box {
    width: 100%;
    height: 200px;
    background: #f8fafc;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    overflow: hidden;
    position: relative;
}

.product-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    transition: transform 0.25s ease;
}

.product-card:hover .product-image {
    transform: scale(1.06);
}

.product-image-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 14px;
    gap: 8px;
}

.image-fallback {
    color: #6c757d;
    font-size: 14px;
    width: 100%;
    text-align: center;
}

.product-name {
    font-size: 18px;
    font-weight: 700;
    color: #0b1220;
    margin: 0 0 6px 0;
    line-height: 1.35;
    letter-spacing: -0.2px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 48px;
}

.product-sku {
    font-size: 13px;
    color: #94a3b8;
    margin: 0 0 16px 0;
    font-family: 'SF Mono', Monaco, monospace;
    letter-spacing: 0.3px;
}

.product-price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    gap: 10px;
}

.product-price {
    font-size: 26px;
    font-weight: 800;
    color: #0b1220;
    letter-spacing: -0.8px;
}

.product-stock-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}

.product-stock-badge.stock-in {
    background: #d1fae5;
    color: #047857;
}

.product-stock-badge.stock-out {
    background: #fee2e2;
    color: #b91c1c;
}

.product-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: auto;
}

.action-btn {
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    padding: 10px 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.view-btn {
    border: 1.5px solid #cbd5e1;
    color: #475569;
    background: #fff;
}

.view-btn:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
}

.components-btn {
    background: #0b1220;
    border-color: #0b1220;
    color: #fff;
}

.components-btn:hover {
    background: #1e293b;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */
@media (max-width: 1400px) {
    .kpi-grid { grid-template-columns: repeat(2, 1fr); }
    .products-grid { grid-template-columns: repeat(3, 1fr); }
    .filter-grid { grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 992px) {
    .catalog-page { width: 95%; }
    .page-title { font-size: 32px; }
    .kpi-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .kpi-value { font-size: 32px; }
    .kpi-icon-wrap { width: 64px; height: 64px; font-size: 28px; margin-bottom: 16px; }
    .kpi-card { padding: 28px 18px 22px; }
    .products-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .filter-grid { grid-template-columns: repeat(2, 1fr); }
    .section-title { font-size: 28px; }
}

@media (max-width: 576px) {
    .catalog-page { width: 100%; padding: 0 12px; }
    .page-title { font-size: 24px; }
    .kpi-grid { grid-template-columns: 1fr; }
    .kpi-value { font-size: 28px; }
    .products-grid { grid-template-columns: 1fr; gap: 16px; }
    .filter-grid { grid-template-columns: 1fr; }
    .product-image-box { height: 220px; }
    .product-actions { grid-template-columns: 1fr; }
    .section-title { font-size: 22px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Products/ProductCatalog.vue